import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useRouter } from "next/router";

import {
  createPosCustomer,
  editCustomerEyeInfo,
  getCustomerInfo,
  getPosCustomer,
  getPosCustomers,
  getPosGuestCustomers,
  updateCustomerEyeInfo,
  updatePosCustomer,
} from "../../core/posCustomer.core";
import { PosUserEyeInfo, PosUserEyeInfoFormData, UpdatePosCustomerData } from "../../types/user";
import { toast } from "react-toastify";
import { useTranslation } from "../../core/translation.core";

export function usePosCustomers({ search, page, pageSize }: { search?: string; page?: number; pageSize?: number }) {
  const { isReady, locale } = useRouter();

  return useQuery({
    enabled: isReady,
    queryFn: () => getPosCustomers({ search, page, pageSize }),
    queryKey: ["posCustomers", locale, search, page, pageSize],
  });
}

export function usePosCustomerById(id: string) {
  const { locale } = useRouter();

  return useQuery({
    enabled: id !== null && id !== undefined && id !== "",
    queryFn: () => getPosCustomer(id),
    queryKey: ["posCustomer", id, locale],
  });
}

export function useAddCustomer() {
  const { t, msg } = useTranslation();
  const { locale } = useRouter();

  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: any) => createPosCustomer(data),
    onError: (err) => {
      toast.error(t(msg.errors.contactAdmin));
      console.error(err);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["posCustomers", locale] });
    },
  });
}

export function useUpdateCustomer() {
  const { t, msg } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation<any, Error, UpdatePosCustomerData>({
    mutationFn: async (data) => updatePosCustomer(data),
    onError: (err) => {
      toast.error(t(msg.errors.contactAdmin));
      console.error(err);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["posCustomers"] });
      queryClient.invalidateQueries({ queryKey: ["posCustomer"] });
    },
  });
}

export function useCustomerInfo(userId: string) {
  return useQuery({
    enabled: userId !== null && userId !== undefined && userId !== "",
    queryFn: () => getCustomerInfo(userId),
    queryKey: ["CustomerInfo", userId],
  });
}

export function useEditCustomerEyeInfo() {
  const queryClient = useQueryClient();

  return useMutation<void, void, { userId: string; data: PosUserEyeInfo }>({
    mutationFn: editCustomerEyeInfo,
    onError: (err) => {
      console.log(err);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["CustomerInfo"] });
    },
  });
}

export function useUpdateCustomerEyeInfo() {
  const { t, msg } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation<void, void, { eyeInfoId: string; data: PosUserEyeInfoFormData }>({
    mutationFn: updateCustomerEyeInfo,
    onError: (err) => {
      toast.error(t(msg.errors.contactAdmin));
      console.error(err);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["CustomerInfo"] });
    },
  });
}

export function usePosGuestCustomer() {
  return useQuery({
    queryFn: getPosGuestCustomers,
    queryKey: ["posGuestUser"],
  });
}
